
import React, { useState } from 'react';
import { AlertConfig } from '../types';
import { Button } from './Button';
import { X, Save, Bell, ShieldAlert, Clock, FileText, AlertTriangle } from 'lucide-react';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    currentSettings?: AlertConfig;
    onSave: (config: AlertConfig) => void;
}

const DEFAULT_CONFIG: AlertConfig = {
    enableOverrun: true,
    enableQuota: true,
    quotaThreshold: 2,
    enableStartDocs: true,
    enableLatency: true,
    latencyDays: 20
};

export const AlertSettingsModal: React.FC<Props> = ({ isOpen, onClose, currentSettings, onSave }) => {
    // Der Dialog wird nur gemountet, solange er offen ist – der Initial-State ist damit der Reset pro Öffnen.
    const [config, setConfig] = useState<AlertConfig>(currentSettings || DEFAULT_CONFIG);

    if (!isOpen) return null;

    const handleChange = (key: keyof AlertConfig, value: any) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Bell className="w-5 h-5 text-blue-400" />
                        Benachrichtigungs-Konfiguration
                    </h3>
                    <button onClick={onClose} className="hover:bg-white/10 p-1 rounded transition-colors"><X className="w-5 h-5"/></button>
                </div>
                
                <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    
                    {/* Section 1: Budget & Quantity */}
                    <div className="space-y-4">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-2">
                            Budget & Mengen
                        </h4>
                        
                        <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                            <div className="pt-1"><AlertTriangle className="w-4 h-4 text-red-500" /></div>
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-bold text-slate-700 text-sm">Mengen-Überschreitung</span>
                                    <input 
                                        type="checkbox" 
                                        checked={config.enableOverrun} 
                                        onChange={e => handleChange('enableOverrun', e.target.checked)}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                </div>
                                <p className="text-xs text-slate-500">
                                    Warnen, wenn die verbrauchte Menge die Vertragsmenge übersteigt (Kritisch).
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                            <div className="pt-1"><ShieldAlert className="w-4 h-4 text-orange-500" /></div>
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-bold text-slate-700 text-sm">Kontingent-Warnung</span>
                                    <input 
                                        type="checkbox" 
                                        checked={config.enableQuota} 
                                        onChange={e => handleChange('enableQuota', e.target.checked)}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="text-xs text-slate-500">Warnen ab Restmenge:</span>
                                    <input 
                                        type="number" 
                                        min="0"
                                        max="10"
                                        value={config.quotaThreshold}
                                        onChange={e => handleChange('quotaThreshold', parseInt(e.target.value) || 0)}
                                        className="w-16 text-center text-sm border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500"
                                        disabled={!config.enableQuota}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Deadlines & Docs */}
                    <div className="space-y-4">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-2">
                            Fristen & Dokumente
                        </h4>

                        <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                            <div className="pt-1"><FileText className="w-4 h-4 text-blue-500" /></div>
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-bold text-slate-700 text-sm">Fehlende Start-Dokumente</span>
                                    <input 
                                        type="checkbox" 
                                        checked={config.enableStartDocs} 
                                        onChange={e => handleChange('enableStartDocs', e.target.checked)}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                </div>
                                <p className="text-xs text-slate-500">
                                    Warnen, wenn "Vorankündigung" oder "Unterlage" bei Projektstart fehlen.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                            <div className="pt-1"><Clock className="w-4 h-4 text-slate-500" /></div>
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-bold text-slate-700 text-sm">Inaktivitäts-Warnung</span>
                                    <input 
                                        type="checkbox" 
                                        checked={config.enableLatency} 
                                        onChange={e => handleChange('enableLatency', e.target.checked)}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="text-xs text-slate-500">Tage ohne Begehung:</span>
                                    <input 
                                        type="number" 
                                        min="1"
                                        value={config.latencyDays}
                                        onChange={e => handleChange('latencyDays', parseInt(e.target.value) || 1)}
                                        className="w-16 text-center text-sm border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500"
                                        disabled={!config.enableLatency}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t border-slate-200 bg-white flex justify-end gap-3">
                    <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
                    <Button variant="primary" onClick={() => onSave(config)} icon={<Save className="w-4 h-4"/>}>
                        Speichern
                    </Button>
                </div>
            </div>
        </div>
    );
};
